import { doc, getDoc } from 'firebase/firestore';

import { db } from './firebase';
import { mapHospitalityReviewDoc } from './mapHospitalityReviewDoc';

/**
 * @param {Map<string, object | null>} cache
 * @param {string} collectionName
 * @param {string} docId
 * @returns {Promise<object | null>}
 */
async function getCachedDocData(cache, collectionName, docId) {
  if (typeof docId !== 'string' || docId.length === 0) {
    return null;
  }
  if (cache.has(docId)) {
    return cache.get(docId);
  }
  try {
    const snapshot = await getDoc(doc(db, collectionName, docId));
    const data = snapshot.exists() ? snapshot.data() : null;
    cache.set(docId, data);
    return data;
  } catch (fetchError) {
    console.error(`Error fetching ${collectionName}/${docId}:`, fetchError);
    cache.set(docId, null);
    return null;
  }
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot} reviewDoc
 * @param {object} [caches]
 * @param {Map<string, object | null>} [caches.breweryCache]
 * @param {Map<string, object | null>} [caches.userCache]
 * @returns {Promise<object>}
 */
export async function enrichHospitalityReviewDoc(reviewDoc, caches = null) {
  const reviewData = reviewDoc.data();
  const breweryId = reviewData.breweryId;
  const userId = reviewData.uid;

  let breweryName = 'Unknown Brewery';
  if (breweryId) {
    if (caches?.breweryCache) {
      const breweryData = await getCachedDocData(caches.breweryCache, 'breweries', breweryId);
      if (breweryData?.name) {
        breweryName = breweryData.name;
      }
    } else {
      try {
        const breweryDoc = await getDoc(doc(db, 'breweries', breweryId));
        if (breweryDoc.exists()) {
          breweryName = breweryDoc.data().name || breweryName;
        }
      } catch (fetchError) {
        console.error(`Error fetching brewery ${breweryId}:`, fetchError);
      }
    }
  }

  let userName = 'Unknown User';
  if (userId) {
    if (caches?.userCache) {
      const userData = await getCachedDocData(caches.userCache, 'users', userId);
      if (userData) {
        userName = userData.userName || userData.displayName || userName;
      }
    } else {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userName = userData.userName || userData.displayName || userName;
        }
      } catch (fetchError) {
        console.error(`Error fetching user ${userId}:`, fetchError);
      }
    }
  }

  return mapHospitalityReviewDoc({
    reviewId: reviewDoc.id,
    reviewData,
    breweryName,
    userName,
  });
}

/**
 * @param {import('firebase/firestore').QueryDocumentSnapshot[]} reviewDocs
 * @returns {Promise<object[]>}
 */
export function enrichHospitalityReviewDocs(reviewDocs) {
  const caches = {
    breweryCache: new Map(),
    userCache: new Map(),
  };
  return Promise.all(
    reviewDocs.map((reviewDoc) => enrichHospitalityReviewDoc(reviewDoc, caches)),
  );
}
